import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { createPortal } from "react-dom";
import { t } from "../lib/sprache";
import { Coco, cocoVorladen } from "./States";
import { cocoUiText, type StationText, type Tr } from "../pages/agentur/coco-rundgang-texte";
import { leerSelektor, rundgangEnde, stationPose, zielSelektor, type Station } from "../pages/agentur/coco-rundgang";

/**
 * Rundgang mit Coco (Inhaber 20.10.2026): Sprechblase mit Coco, Schrittzähler, Weiter/Zurück/Überspringen und eine Umrandung um
 * den echten Knopf der Seite (data-coco, coco-rundgang.ts). Die Blase ist ein modaler <dialog> wie die übrigen Dialoge: der Fokus
 * bleibt in der Blase, Esc überspringt, die Seite dahinter ist gesperrt. Findet die Station ihr Ziel nicht (Modul oder Recht fehlt),
 * erscheint sie ohne Umrandung, nie mit einer Fehlermeldung. Am Handy liegt die Blase unten über der Navigation, das Ziel wird
 * darüber in den sichtbaren Bereich geholt. Reduzierte Bewegung: ohne Übergänge (app.css) und ohne weiches Scrollen.
 *
 * K-573 (Inhaber 21.10.2026: „da steht Hi ich bin Coco, aber man sieht Coco nicht mal“):
 *  – Coco groß und sofort sichtbar: Bilder „eager“ und beim Start vorgeladen; Begrüßung winkend (112 px), danach 72 px, zum
 *    Abschluss freudig; am Handy kleiner (app.css).
 *  – Leere Seiten: steht zum Ziel ein leerer Zustand auf der Seite (data-coco-leer) oder ist der Knopf deaktiviert, wird nicht
 *    der Knopf hervorgehoben, sondern der leere Bereich bzw. nichts; der Text erklärt dann, was dort später erscheint (leer).
 *  – Die Blase verdeckt nie das Ziel: darunter, darüber, rechts oder links; passt nichts, wird das Ziel zuerst so gescrollt, dass
 *    Ziel und Blase übereinander passen (dafür hält der Inhalt während des Rundgangs unten Luft frei); ist das Ziel dafür zu hoch,
 *    wird nur sein Kopf (data-coco-kopf) hervorgehoben.
 *  – Während des Rundgangs trägt <html> die Klasse tk-rundgang-an: Test- und Start-Banner bleiben eingeklappt (Ruhe oben).
 */

/** Wie lange eine Station auf ihr Ziel wartet (Seite lädt noch), bevor sie ohne Umrandung erscheint */
const ZIEL_WARTEN_MS = 1500;
/** K-551: Die Blase erscheint erst, wenn das Ziel nach dem Scrollen ruht (so viele Messungen im Abstand von 50 ms ohne Bewegung),
 * höchstens nach STEHT_MAX_MS. Zeitgeber statt requestAnimationFrame: der steht in verdeckten Fenstern still, die Blase käme nie */
const STEHT_MESSUNGEN = 3;
const STEHT_MAX_MS = 2500;
const HANDY = "(max-width: 767px)";
/** Abstand der Umrandung zum Ziel, der Blase zur Umrandung und zum Fensterrand */
const LUFT = 6;
const ABSTAND = 12;
const RAND = 16;

const reduziert = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const handy = () => typeof window !== "undefined" && !!window.matchMedia?.(HANDY).matches;

/** Ziel sichtbar und nicht weggeklappt */
function sichtbar(el: Element | null): el is HTMLElement {
  if (!(el instanceof HTMLElement) || el.closest("[hidden], [inert]")) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Sichtbarer Bereich (Layout-Viewport); innerHeight lieferte in der Handy-Ansicht der Entwicklertools ein Vielfaches */
const fensterH = () => document.documentElement.clientHeight || window.innerHeight;
const fensterB = () => document.documentElement.clientWidth || window.innerWidth;

/** Knopf, der noch nichts tun kann (grau): wird nie hervorgehoben */
const istAus = (el: HTMLElement) => el.matches(":disabled, [aria-disabled='true']");

/** Unterkante des Seitenkopfs (Logo, Glocke), soweit er im Bild steht: darunter beginnt der Platz für Ziel und Blase */
function kopfUnten(): number {
  const k = document.querySelector(".portal-header")?.getBoundingClientRect();
  return k ? Math.max(0, Math.min(k.bottom, fensterH() / 3)) : 0;
}

interface Rahmen { top: number; left: number; width: number; height: number }
interface Box { top: number; left: number; bottom: number; right: number; width: number; height: number }

const umrandung = (r: DOMRect): Box => ({ top: r.top - LUFT, left: r.left - LUFT, bottom: r.bottom + LUFT, right: r.right + LUFT, width: r.width + LUFT * 2, height: r.height + LUFT * 2 });

/** Lage der Blase neben dem Ziel, ohne es zu verdecken (Desktop); null, wenn sie nirgends daneben passt */
export function lageNeben(z: Box, b: { width: number; height: number }, fenster: { width: number; height: number; oben: number }): { top: number; left: number } | null {
  const x = (left: number) => Math.min(Math.max(RAND, left), fenster.width - b.width - RAND);
  const y = (top: number) => Math.min(Math.max(fenster.oben + RAND, top), fenster.height - b.height - RAND);
  if (z.bottom + ABSTAND + b.height + RAND <= fenster.height) return { top: z.bottom + ABSTAND, left: x(z.left) };
  if (z.top - ABSTAND - b.height >= fenster.oben + RAND) return { top: z.top - ABSTAND - b.height, left: x(z.left) };
  const hochGenug = b.height + 2 * RAND <= fenster.height - fenster.oben;
  if (hochGenug && z.right + ABSTAND + b.width + RAND <= fenster.width) return { top: y(z.top), left: z.right + ABSTAND };
  if (hochGenug && z.left - ABSTAND - b.width >= RAND) return { top: y(z.top), left: z.left - ABSTAND - b.width };
  return null;
}

/**
 * Was hervorgehoben wird: das Ziel selbst oder, wenn es mit der Blase nicht ins Fenster passt (Liste „So startest du“ bei 1024 × 768
 * oder am Handy), nur sein Kopf (alle data-coco-kopf darin). Je Bild neu entschieden, weil die Blase mit ihrem Inhalt wachsen kann.
 */
function zielRechteck(el: HTMLElement, b: DOMRect | undefined): DOMRect {
  const ganz = el.getBoundingClientRect();
  const kopf = [...el.querySelectorAll<HTMLElement>("[data-coco-kopf]")];
  if (!kopf.length) return ganz;
  const z = umrandung(ganz);
  const f = { width: fensterB(), height: fensterH(), oben: kopfUnten() };
  const mass = { width: b?.width ?? 440, height: b?.height ?? 300 };
  const passt = handy() ? z.height + RAND <= (b?.top ?? f.height * 0.5) - f.oben
    : passtUebereinander(z, mass, f) || z.width + ABSTAND + mass.width + 2 * RAND <= f.width;
  return passt ? ganz : vereint(kopf) ?? ganz;
}

/** Umriss mehrerer Elemente; Elemente mit display: contents (Kopfzeile im Raster der Startliste) über ihre Kinder */
function vereint(els: HTMLElement[]): DOMRect | null {
  const rs = els.flatMap((e) => (getComputedStyle(e).display === "contents" ? [...e.children] : [e])).map((e) => e.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
  if (!rs.length) return null;
  const top = Math.min(...rs.map((r) => r.top)), left = Math.min(...rs.map((r) => r.left));
  return new DOMRect(left, top, Math.max(...rs.map((r) => r.right)) - left, Math.max(...rs.map((r) => r.bottom)) - top);
}

/** Passen Ziel und Blase übereinander ins Fenster (nach Scrollen)? */
function passtUebereinander(z: Box, b: { height: number }, fenster: { height: number; oben: number }): boolean {
  return z.height + ABSTAND + b.height + 2 * RAND <= fenster.height - fenster.oben;
}

export function RundgangBlase(props: {
  stationen: readonly Station[];
  index: number;
  /** leer: die Station zeigt den leeren Bereich statt des Knopfs (bzw. der Knopf ist noch grau) */
  text: (s: Station, info: { leer: boolean }) => StationText;
  t: Tr;
  onSchritt: (index: number) => void;
  onEnde: (status: "beendet" | "uebersprungen", index: number) => void;
  /** zusätzliche Knöpfe an der letzten Station (Abschluss) */
  abschluss?: ReactNode;
  /** an der letzten Station ohne „Fertig“ (der Abschluss hat eigene Knöpfe, die den Rundgang beenden) */
  ohneFertig?: boolean;
  /** „Fertig“ bleibt Hauptknopf, obwohl es Abschluss-Knöpfe gibt (die dann alle Nebenknöpfe sind) */
  fertigPrimaer?: boolean;
  /** K-573: Wahl an der ersten Station statt „Weiter“ (Ohne Beispiele / Mit Beispieldaten zeigen), mit kurzem Hinweis darüber */
  wahl?: ReactNode;
  wahlHinweis?: string;
}) {
  const { stationen, index, t, onSchritt, onEnde } = props;
  const station = stationen[index]!;
  const ui = cocoUiText(t);
  const titelId = useId();
  const textId = useId();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dialog = useRef<HTMLDialogElement>(null);
  const blase = useRef<HTMLDivElement>(null);
  const weiter = useRef<HTMLButtonElement>(null);
  // Ziel und „bereit“ gelten je Station: beim Wechsel ist die neue Station sofort „nicht bereit“ (kein Bild mit neuem Text an alter Stelle)
  const [stand, setStand] = useState<{ index: number; ziel: HTMLElement | null; leer: boolean } | null>(null);
  const bereit = stand?.index === index;
  const ziel = bereit ? stand.ziel : null;
  const leer = bereit && stand.leer;
  const maskeId = `tk-rg-maske-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [rahmen, setRahmen] = useState<Rahmen | null>(null);
  const [lage, setLage] = useState<CSSProperties>({});
  const letzte = index === stationen.length - 1;
  const wahl = index === 0 ? props.wahl : undefined;
  const amOrt = !station.pfad || pathname.replace(/\/+$/, "") === station.pfad;

  // K-573: alle Posen sofort laden, Ruhe oben (Banner eingeklappt, app.css) und unten Luft zum Scrollen, solange der Rundgang läuft
  useLayoutEffect(() => {
    cocoVorladen(["winkt", "erklaert", "hinweis", "freut-sich"]);
    const root = document.documentElement;
    root.classList.add("tk-rundgang-an");
    return () => root.classList.remove("tk-rundgang-an");
  }, []);

  // Zur Seite der Station, aber nur beim Schrittwechsel. Verlässt jemand die Seite selbst (Seitenleiste, Link aus einer Mail),
  // hält der Rundgang an, statt ihn zurückzuziehen (Prüf-Chat 30.10.2026: Deep-Links landeten sonst auf der Übersicht)
  const schrittGefuehrt = useRef<number | null>(null);
  const warAmOrt = useRef(false);
  useEffect(() => {
    if (schrittGefuehrt.current !== index) {
      schrittGefuehrt.current = index;
      warAmOrt.current = amOrt;
      if (!amOrt && station.pfad) navigate(station.pfad);
    } else if (amOrt) {
      warAmOrt.current = true;
    } else if (warAmOrt.current) {
      rundgangEnde();
    }
  }, [index, amOrt]);

  // Ziel suchen, solange die Seite lädt; danach mit oder ohne Umrandung zeigen
  useEffect(() => {
    setRahmen(null);
    if (!amOrt) return;
    if (!station.ziel) { setStand({ index, ziel: null, leer: false }); return; }
    const zielWert = station.ziel;
    const start = Date.now();
    let timer = 0;
    /** Was die Station hervorhebt: leerer Zustand vor Knopf; grauer Knopf: nichts (leer); zu hohes Ziel: sein Kopf */
    const finde = (): { el: HTMLElement | null; leer: boolean; gefunden: boolean } => {
      const l = document.querySelector(leerSelektor(zielWert));
      if (sichtbar(l)) return { el: l, leer: true, gefunden: true };
      const z = document.querySelector(zielSelektor(zielWert));
      if (sichtbar(z)) return istAus(z) ? { el: null, leer: true, gefunden: true } : { el: z, leer: false, gefunden: true };
      return { el: null, leer: false, gefunden: false };
    };
    const fenster = () => ({ width: fensterB(), height: fensterH(), oben: kopfUnten() });
    const blasenMass = () => {
      const b = blase.current?.getBoundingClientRect();
      return { width: b?.width ?? 420, height: b?.height ?? 260 };
    };
    // nach dem Scrollen warten, bis das Ziel ruht UND im sichtbaren Bereich steht; erst dann Blase und Aussparung zeigen.
    // K-558 b: am Handy lud die Seite oben noch nach (AGB-Kasten), das Ziel rutschte aus dem Bild, die Blase stand schon darüber.
    // Steht das Ziel nicht im Bild (am Handy: obere Hälfte über der Blase), wird sofort (ohne weiches Scrollen) nachgescrollt.
    const imBild = (r: DOMRect) => (handy() ? r.top >= 0 && r.top <= fensterH() * 0.4 : r.top >= 0 && r.top < fensterH());
    const ruht = (anfang: { el: HTMLElement; leer: boolean }) => {
      const seit = Date.now();
      let akt = anfang;
      let vorher = NaN;
      let still = 0;
      let nachgescrollt = 0;
      let angepasst = false;
      const pruefe = () => {
        // die Seite lädt nach (leerer Zustand weicht der Liste oder umgekehrt): neu wählen und von vorn warten
        const neu = finde();
        if (!neu.el) { setStand({ index, ziel: null, leer: neu.leer }); return; }
        if (neu.el !== akt.el) { akt = { el: neu.el, leer: neu.leer }; still = 0; vorher = NaN; }
        const el = akt.el;
        if (Date.now() - seit >= STEHT_MAX_MS) { setStand({ index, ziel: el, leer: akt.leer }); return; }
        const r = zielRechteck(el, blase.current?.getBoundingClientRect());
        // höchstens dreimal nachscrollen (ein Ziel ganz unten auf der Seite kommt nie ganz nach oben)
        if (!imBild(r) && still > 0 && nachgescrollt < 3) {
          nachgescrollt += 1;
          el.scrollIntoView({ block: handy() ? "start" : "center", behavior: "auto" });
          still = 0; vorher = NaN; timer = window.setTimeout(pruefe, 50); return;
        }
        const top = Math.round(r.top);
        still = top === vorher ? still + 1 : 0;
        vorher = top;
        if (still >= STEHT_MESSUNGEN) {
          // K-573: passt die Blase nirgends neben das Ziel, das Ziel einmal so weit nach oben holen, dass beide übereinander passen
          const f = fenster();
          const z = umrandung(r);
          const b = blasenMass();
          if (!handy() && !angepasst && !lageNeben(z, b, f) && passtUebereinander(z, b, f)) {
            angepasst = true;
            window.scrollBy({ top: z.top - (f.oben + RAND), behavior: "auto" });
            still = 0; vorher = NaN; timer = window.setTimeout(pruefe, 50); return;
          }
          setStand({ index, ziel: el, leer: akt.leer });
          return;
        }
        timer = window.setTimeout(pruefe, 50);
      };
      timer = window.setTimeout(pruefe, 50);
    };
    const suche = () => {
      const f = finde();
      if (f.el) {
        // am Handy sofort (weiches Scrollen lief dort gegen das Nachladen der Seite), am Desktop weich
        f.el.scrollIntoView({ block: handy() ? "start" : "center", behavior: reduziert() || handy() ? "auto" : "smooth" });
        ruht({ el: f.el, leer: f.leer });
        return;
      }
      if (f.gefunden || Date.now() - start >= ZIEL_WARTEN_MS) { setStand({ index, ziel: null, leer: f.leer }); return; }
      timer = window.setTimeout(suche, 100);
    };
    suche();
    return () => window.clearTimeout(timer);
  }, [index, amOrt, station.ziel]);

  // Blase als modaler Dialog: öffnen, solange der Rundgang läuft
  useEffect(() => {
    const d = dialog.current;
    if (d && !d.open) d.showModal();
    return () => { if (d?.open) d.close(); };
  }, []);

  // Fokus in die Blase, sobald die Station steht: auf den Hauptknopf (Weiter, an der ersten Station „Mit Beispieldaten zeigen“,
  // am Abschluss „Mit dem nächsten Schritt weitermachen“ bzw. „Beispieldaten behalten“)
  useEffect(() => {
    if (!bereit) return;
    const haupt = blase.current?.querySelector<HTMLElement>(".tk-rundgang-knoepfe button.primary:not([disabled])");
    (haupt ?? weiter.current)?.focus({ preventScroll: true });
  }, [bereit, index, !!wahl]);

  // Umrandung und Lage der Blase folgen dem Ziel: je Bild nachmessen (Scrollen, weiches Scrollen, nachladende Inhalte, Größe);
  // neu gesetzt wird nur, was sich geändert hat
  useLayoutEffect(() => {
    let frame = 0;
    let vorher = "";
    // höchstens zweimal je Station nachrücken (wächst oder schrumpft die Blase, etwa wenn Beispieldaten-Knöpfe nachladen)
    let nachgerueckt = 0;
    const messen = () => {
      const b = blase.current?.getBoundingClientRect();
      const r = ziel && sichtbar(ziel) ? zielRechteck(ziel, b) : null;
      const z = r ? umrandung(r) : null;
      const neuRahmen = z ? { top: z.top, left: z.left, width: z.width, height: z.height } : null;
      let neuLage: CSSProperties = {};
      if (!handy() && b) {
        const f = { width: fensterB(), height: fensterH(), oben: kopfUnten() };
        if (!z) neuLage = { top: Math.max(RAND, (f.height - b.height) / 2), left: Math.max(RAND, (f.width - b.width) / 2) };
        else {
          const neben = lageNeben(z, b, f);
          if (!neben && bereit && nachgerueckt < 2 && passtUebereinander(z, b, f) && Math.abs(z.top - (f.oben + RAND)) > 2) {
            nachgerueckt += 1;
            window.scrollBy({ top: z.top - (f.oben + RAND), behavior: "auto" });
            frame = requestAnimationFrame(messen);
            return;
          }
          if (neben) neuLage = neben;
          else {
            // passt nirgends (Ziel höher als das Fenster): dort, wo mehr Platz ist, so wenig wie möglich darüber
            const unten = f.height - z.bottom;
            const top = unten >= z.top - f.oben ? Math.min(z.bottom + ABSTAND, f.height - b.height - RAND) : Math.max(f.oben + RAND, z.top - ABSTAND - b.height);
            neuLage = { top, left: Math.min(Math.max(RAND, z.left), f.width - b.width - RAND) };
          }
        }
      }
      const schluessel = JSON.stringify([neuRahmen, neuLage]);
      if (schluessel !== vorher) {
        vorher = schluessel;
        setRahmen(neuRahmen);
        setLage(neuLage);
      }
      frame = requestAnimationFrame(messen);
    };
    messen();
    return () => cancelAnimationFrame(frame);
  }, [ziel, bereit, index]);

  // Fokus bleibt in der Blase (Tab und Umschalt+Tab laufen im Kreis)
  const tasten = (e: KeyboardEvent) => {
    if (e.key !== "Tab" || !blase.current) return;
    const k = [...blase.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]")];
    if (!k.length) return;
    const erst = k[0]!, letzt = k[k.length - 1]!;
    if (e.shiftKey && document.activeElement === erst) { e.preventDefault(); letzt.focus(); }
    else if (!e.shiftKey && document.activeElement === letzt) { e.preventDefault(); erst.focus(); }
  };

  const text = props.text(station, { leer });
  const pose = stationPose(index, stationen.length, leer);
  const gross = pose === "winkt" || pose === "freut-sich";
  return (
    <>
      <dialog ref={dialog} className="tk-rundgang" aria-labelledby={titelId} aria-describedby={textId}
        onCancel={(e) => { e.preventDefault(); onEnde("uebersprungen", index); }} onKeyDown={tasten}>
        {/* Umrandung im Dialog (oberste Ebene): im Inhalt schnitten Vorfahren mit transform/contain den Schatten ab */}
        {/* K-551: abgedunkelt wird als eine Fläche mit Aussparung um das Ziel (SVG-Maske); vier Streifen ließen helle Nahtlinien */}
        <svg className="tk-rundgang-dimm" aria-hidden="true" focusable="false">
          <defs>
            <mask id={maskeId}>
              <rect width="100%" height="100%" fill="white" />
              {rahmen && bereit && <rect x={rahmen.left} y={rahmen.top} width={rahmen.width} height={rahmen.height} rx={10} fill="black" />}
            </mask>
          </defs>
          <rect width="100%" height="100%" mask={`url(#${maskeId})`} />
        </svg>
        {rahmen && bereit && <div className="tk-rundgang-ring" aria-hidden="true" style={rahmen} />}
        {/* erst sichtbar, wenn das Ziel steht; neu eingeblendet je Station (key), nie halb durchsichtig über laufender Seite */}
        <div ref={blase} key={bereit ? `da-${index}` : "warten"} className={`tk-rundgang-blase panel stack${index === 0 || (letzte && props.abschluss) ? " tk-rundgang-blase-breit" : ""}`}
          style={{ ...lage, visibility: bereit ? "visible" : "hidden" }} data-station={station.id} data-leer={leer ? "" : undefined}>
          <div className="tk-rundgang-inhalt">
            <Coco pose={pose} size={gross ? 112 : 72} eager className={`tk-rundgang-coco${gross ? " gross" : ""}`} />
            <div className="stack tk-rundgang-texte">
              <div className="eyebrow">{ui.eyebrow} · <span className="tk-rundgang-stand">{ui.schritt(index + 1, stationen.length)}</span></div>
              <h2 id={titelId}>{text.titel}</h2>
              <p id={textId}>{text.text}</p>
              {text.hinweis && <small className="tk-rundgang-hinweis">{text.hinweis}</small>}
            </div>
          </div>
          <div className="tk-rundgang-punkte" aria-hidden="true">{stationen.map((s, i) => <span key={s.id} className={i === index ? "aktiv" : i < index ? "fertig" : undefined} />)}</div>
          {wahl && props.wahlHinweis && <small className="tk-rundgang-hinweis">{props.wahlHinweis}</small>}
          <div className="tk-rundgang-knoepfe">
            {!letzte && <button type="button" className="quiet" onClick={() => onEnde("uebersprungen", index)}>{ui.ueberspringen}</button>}
            <span className="tk-rundgang-luecke" />
            {index > 0 && <button type="button" onClick={() => { if (bereit) onSchritt(index - 1); }}>{ui.zurueck}</button>}
            {/* K-573: Abschluss-Knöpfe in derselben Zeile (die Blase bleibt niedrig, die Liste daneben sichtbar) */}
            {/* „Fertig“ steht vor den Abschluss-Knöpfen, der Hauptknopf ganz rechts */}
            {!wahl && !(letzte && props.ohneFertig) && (
              <button ref={weiter} type="button" className={letzte && props.abschluss && !props.fertigPrimaer ? undefined : "primary"}
                onClick={() => { if (bereit) (letzte ? onEnde("beendet", index) : onSchritt(index + 1)); }}>
                {letzte ? ui.fertig : ui.weiter}
              </button>
            )}
            {letzte && props.abschluss}
            {wahl}
          </div>
        </div>
      </dialog>
    </>
  );
}

/** Coco lugt unten rechts herein (wie Trudi im Altportal); holt die Erklärung der Seite zurück. Am Handy im Chat ausgeblendet. */
export function CocoLugt({ label, onClick, stoertAmHandy }: { label: string; onClick: () => void; stoertAmHandy?: boolean }) {
  // G4 (D-1, Inhaber 21.10.2026): Coco sitzt als kleiner Knopf im Seitenkopf vor den Aktionen, statt unten rechts über
  // Tabellenknöpfen und neben dem Chat-Knopf zu liegen. Seiten ohne Seitenkopf behalten Coco unten rechts.
  const { pathname } = useLocation();
  const [ziel, setZiel] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    let host: HTMLElement | null = null;
    const finde = () => {
      const kopf = document.querySelector<HTMLElement>(".pagehead");
      if (!kopf) return false;
      host = document.createElement("span");
      host.className = "tk-coco-kopf-host";
      // D-2 (Inhaber 21.10.2026): als kleines Fragezeichen direkt neben dem Titel, nicht mehr zwischen den Kopfaktionen.
      // D2-2: neben, nicht in der h1, sonst landet das „?“ im Tab-Titel (pageTitle liest h1.textContent) und im Vorlesetext
      const titel = kopf.querySelector<HTMLElement>("h1");
      if (titel) titel.insertAdjacentElement("afterend", host);
      else kopf.appendChild(host);
      setZiel(host);
      return true;
    };
    const timer = finde() ? undefined : window.setTimeout(finde, 400);
    return () => { window.clearTimeout(timer); host?.remove(); setZiel(null); };
  }, [pathname]);
  useLayoutEffect(() => {
    if (ziel) return;
    document.documentElement.classList.add("tk-coco-lugt-da");
    return () => document.documentElement.classList.remove("tk-coco-lugt-da");
  }, [ziel]);
  if (ziel) {
    return createPortal(
      <button type="button" className="tk-coco-kopf tk-noprint" onClick={onClick} aria-label={label} title={label}>
        <span aria-hidden="true">?</span>
      </button>, ziel);
  }
  return (
    <button type="button" className={`tk-coco-lugt tk-noprint${stoertAmHandy ? " tk-coco-lugt-handy-aus" : ""}`} onClick={onClick} aria-label={label} title={label}>
      <img src="/coco/coco-lugt.svg" width={84} height={49} alt="" aria-hidden="true" decoding="async" />
    </button>
  );
}
